import { useCallback, useMemo } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconBack,
  IconCheckCircle,
  IconClose,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { FormContainer, Input, MainContainer } from '~/components/utility';
import { ApiStatus, ErrorCode } from '~/constants';
import { useApiError, useCareTeamConnectionError } from '~/hooks';
import { type RootState } from '~/reducers';
import { addCareTeamConnection } from '~/services/clm';
import { buildTestId } from '~/utilities/buildTestId';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  env: { appVersion, deviceId, osType, osVersion },
  auth: { account_id: accountId },
  nav: { query },
}: RootState) => ({
  appVersion,
  deviceId,
  osType,
  osVersion,
  accountId,
  query,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const validationSchema = z.object({
  clinicId: z
    .string('Enter a valid clinic ID')
    // Min 8 characters
    .min(8, 'Enter a valid clinic ID')
    // Max 16 characters
    .max(16, 'Enter a valid clinic ID')
    // Alphanumeric
    .regex(/^[a-zA-Z0-9]+$/, 'Enter a valid clinic ID'),
});

type FormValues = z.output<typeof validationSchema>;

const initialFormValues: FormValues = {
  clinicId: '',
};

const testID = 'care-team-connections-form';

const CareTeamConnectionsForm = ({
  accountId,
  appVersion,
  deviceId,
  osType,
  osVersion,
  query,
}: Props) => {
  const {
    data: clinicData,
    isPending,
    isSuccess,
    mutateAsync,
  } = useMutation({
    mutationKey: ['addCareTeamConnection', accountId],
    mutationFn: async (connection: {
      clinicId: string;
      patientId: string;
      appVersion?: string;
      osType?: string;
      osVersion?: string;
    }) => {
      const response = await addCareTeamConnection({
        ...connection,
        deviceId,
      });

      return response.clinic;
    },
    onError: (error: AxiosError<ApiErrorData>) => {
      handleApiRequestError(error);
    },
  });

  const {
    showAlreadyConnectedErrorModal,
    showAlreadyInvitedErrorModal,
    showClinicNotFoundErrorModal,
  } = useCareTeamConnectionError();
  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const onSubmitForm = async (values: FormValues) => {
    await mutateAsync({
      clinicId: values.clinicId,
      patientId: accountId,
      appVersion,
      osType,
      osVersion,
    });
  };

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldError,
    isValid,
    dirty,
    errors,
    touched,
    values,
  } = useFormik({
    initialValues: initialFormValues,
    onSubmit: onSubmitForm,
    validationSchema: toFormikValidationSchema(validationSchema),
    validateOnBlur: true,
  });

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      switch (error.response?.data.code ?? error.response?.status) {
        case ApiStatus.ALREADY_SENT_INVITE_TO_CLINIC:
          setFieldError('clinicId', '');
          showAlreadyInvitedErrorModal();
          break;
        case ApiStatus.CONFLICT:
          setFieldError('clinicId', '');
          showAlreadyConnectedErrorModal();
          break;
        case ApiStatus.NOT_FOUND:
          setFieldError('clinicId', '');
          showClinicNotFoundErrorModal();
          break;
        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      setFieldError,
      showAlreadyConnectedErrorModal,
      showAlreadyInvitedErrorModal,
      showClinicNotFoundErrorModal,
      showApiErrorModal,
      showNetworkErrorModal,
    ],
  );

  const isFormSubmittable = values.clinicId && isValid && !isPending && dirty;

  const handleClickBack = useCallback(() => {
    mediator.publish('router:back');
  }, []);

  const handleDone = useCallback(() => {
    mediator.publish('router:navigate', {
      url: '/connections',
      query: { ...query },
    });
  }, [query]);

  const buttonStart = useMemo(() => {
    const buttonTestID = buildTestId({ testID, suffix: ['nav', 'back'] });

    return isSuccess
      ? {
          element: <IconClose testID={buttonTestID} />,
          label: 'Done',
          onPress: handleClickBack,
        }
      : {
          element: <IconBack testID={buttonTestID} />,
          label: 'Back',
          onPress: handleClickBack,
        };
  }, [handleClickBack, isSuccess]);

  return (
    <MainContainer testID={testID}>
      {isPending && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({
            testID,
            suffix: ['loading', 'modal'],
          })}
        />
      )}
      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        index={isSuccess ? 1 : 0}
        topNavigationProps={{
          title: 'Add a Care Team',
          buttonStart,
          testID: buildTestId({ testID, suffix: 'nav' }),
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: ['form', 'page'] })}
        >
          <InformationalScreen.Content>
            <FormContainer>
              <Text marginTop={-8}>
                If your care team provided you with their Clinic ID, enter it
                below.
              </Text>

              <Input
                aria-label="Clinic ID"
                placeholder="Clinic ID"
                label="Clinic ID"
                errorMessage={errors.clinicId}
                isInvalid={Boolean(errors.clinicId && touched.clinicId)}
                testID={buildTestId({
                  testID,
                  suffix: ['clinic', 'id', 'input'],
                })}
                value={values.clinicId}
                onChangeText={handleChange('clinicId')}
                onBlur={handleBlur('clinicId')}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label="Connect"
              disabled={!isFormSubmittable}
              onPress={() => {
                handleSubmit();
              }}
              testID={buildTestId({ testID, suffix: ['submit', 'button'] })}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>

        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: ['success', 'page'] })}
        >
          <InformationalScreen.Content>
            <Stack alignContent="center" alignItems="center" marginBottom="$2">
              <IconCheckCircle
                color="$support.positive.default"
                accentColor="$neutral.noChangeWhite"
                style={{ width: 72, height: 72 }}
                testID={buildTestId({ testID, suffix: ['success', 'icon'] })}
              />
            </Stack>
            <Text>
              Your request to connect was sent to:
              <br />
              <Text
                fontWeight="bold"
                testID={buildTestId({ testID, suffix: ['clinic', 'info'] })}
              >
                {clinicData?.name} - {clinicData?.id}
              </Text>
              <br />
              <br />
              You&apos;ll be notified when your care team accepts your request.
            </Text>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label="Done"
              onPress={handleDone}
              testID={buildTestId({ testID, suffix: ['done', 'button'] })}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(CareTeamConnectionsForm);
